import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  DocumentArrowDownIcon,
  PrinterIcon,
  ShareIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  UserIcon,
  BanknotesIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const PayslipDetail = () => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  // Mock payslip data - in real app this would come from API based on route params
  const payslipData = {
    employeeInfo: {
      name: 'Rajesh Kumar',
      employeeId: 'EMP001234',
      designation: 'Senior Software Engineer',
      department: 'Information Technology',
      joiningDate: '2022-03-15',
      pan: 'ABCDE1234F',
      pfNumber: 'PF12345678',
      esiNumber: 'ESI987654321'
    },
    companyInfo: {
      name: 'Apollo Healthcare Portal',
      address: '123 Healthcare Street, Medical District, Mumbai - 400001',
      pfNumber: 'COMP/PF/123456',
      esiNumber: 'COMP/ESI/789012'
    },
    payPeriod: {
      month: 'July 2025',
      payDate: '2025-07-31',
      workingDays: 22,
      actualDays: 21,
      leaveDays: 1
    },
    earnings: [
      { component: 'Basic Salary', amount: 50000, type: 'fixed' },
      { component: 'House Rent Allowance', amount: 20000, type: 'fixed' },
      { component: 'Transport Allowance', amount: 5000, type: 'fixed' },
      { component: 'Medical Allowance', amount: 3000, type: 'fixed' },
      { component: 'Special Allowance', amount: 7000, type: 'fixed' },
      { component: 'Overtime', amount: 3500, type: 'variable' },
      { component: 'Performance Bonus', amount: 5000, type: 'variable' }
    ],
    deductions: [
      { component: 'Provident Fund (Employee)', amount: 6000, type: 'statutory' },
      { component: 'Professional Tax', amount: 200, type: 'statutory' },
      { component: 'Income Tax (TDS)', amount: 4800, type: 'tax' },
      { component: 'ESI (Employee)', amount: 375, type: 'statutory' },
      { component: 'Health Insurance Premium', amount: 1125, type: 'voluntary' }
    ],
    employerContributions: [
      { component: 'Provident Fund (Employer)', amount: 6000 },
      { component: 'ESI (Employer)', amount: 1313 },
      { component: 'Gratuity', amount: 2404 }
    ]
  };

  const calculateTotals = () => {
    const totalEarnings = payslipData.earnings.reduce((sum, item) => sum + item.amount, 0);
    const totalDeductions = payslipData.deductions.reduce((sum, item) => sum + item.amount, 0);
    const netSalary = totalEarnings - totalDeductions;
    
    return { totalEarnings, totalDeductions, netSalary };
  };

  const { totalEarnings, totalDeductions, netSalary } = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleDownload = () => {
    setIsDownloading(true);
    // Simulate download
    setTimeout(() => {
      setIsDownloading(false);
      // In real app, trigger PDF download
      alert('Payslip downloaded successfully!');
    }, 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Payslip - ${payslipData.payPeriod.month}`,
        text: `Payslip for ${payslipData.payPeriod.month}`,
        url: window.location.href,
      });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <button
                onClick={() => navigate('/payroll')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Payroll
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payslip Detail</h1>
                <p className="mt-1 text-base sm:text-lg text-gray-600">{payslipData.payPeriod.month}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 sm:space-x-3">
              <button
                onClick={handleShare}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Share
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-apollo-primary hover:bg-apollo-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary disabled:opacity-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {isDownloading ? 'Downloading...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>

        {/* Payslip Content */}
        <div className="bg-white shadow-lg rounded-lg border border-gray-200 p-4 sm:p-6 lg:p-8 print:shadow-none print:border-none">
          {/* Company Header */}
          <div className="border-b-2 border-apollo-primary pb-4 sm:pb-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-apollo-primary">{payslipData.companyInfo.name}</h2>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">{payslipData.companyInfo.address}</p>
              </div>
              <div className="text-left sm:text-right text-sm text-gray-600">
                <div>PF Number: {payslipData.companyInfo.pfNumber}</div>
                <div>ESI Number: {payslipData.companyInfo.esiNumber}</div>
              </div>
            </div>
          </div>

          {/* Employee and Pay Period Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6 sm:mb-8">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-apollo-primary" />
                Employee Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Employee ID:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.employeeId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Designation:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.designation}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Department:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.department}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PAN:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.pan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PF Number:</span>
                  <span className="font-medium text-right">{payslipData.employeeInfo.pfNumber}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-apollo-primary" />
                Pay Period Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pay Period:</span>
                  <span className="font-medium text-right">{payslipData.payPeriod.month}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pay Date:</span>
                  <span className="font-medium text-right">{payslipData.payPeriod.payDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Working Days:</span>
                  <span className="font-medium text-right">{payslipData.payPeriod.workingDays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Days Worked:</span>
                  <span className="font-medium text-right">{payslipData.payPeriod.actualDays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Leave Days:</span>
                  <span className="font-medium text-right">{payslipData.payPeriod.leaveDays}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Earnings and Deductions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6 sm:mb-8">
            {/* Earnings */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-500" />
                Earnings
              </h3>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="space-y-3">
                  {payslipData.earnings.map((earning, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-700 pr-2">{earning.component}</span>
                      <span className={`font-medium text-sm ${earning.type === 'variable' ? 'text-blue-600' : 'text-gray-900'}`}>
                        {formatCurrency(earning.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-green-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900 text-sm">Total Earnings</span>
                      <span className="font-bold text-green-600 text-sm">{formatCurrency(totalEarnings)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
                Deductions
              </h3>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="space-y-3">
                  {payslipData.deductions.map((deduction, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-700 pr-2">{deduction.component}</span>
                      <span className={`font-medium text-sm ${deduction.type === 'tax' ? 'text-orange-600' : 'text-gray-900'}`}>
                        {formatCurrency(deduction.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-red-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900 text-sm">Total Deductions</span>
                      <span className="font-bold text-red-600 text-sm">{formatCurrency(totalDeductions)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Net Salary */}
          <div className="bg-apollo-primary rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-white space-y-2 sm:space-y-0">
              <span className="text-lg sm:text-xl font-semibold">Net Salary</span>
              <span className="text-2xl sm:text-3xl font-bold">{formatCurrency(netSalary)}</span>
            </div>
            <div className="mt-2 text-apollo-primary-100 text-sm">
              Amount to be credited to your account
            </div>
          </div>

          {/* Employer Contributions */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <BuildingOfficeIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-apollo-primary" />
              Employer Contributions (For Information Only)
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-3">
                {payslipData.employerContributions.map((contribution, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-700 pr-2">{contribution.component}</span>
                    <span className="font-medium text-gray-900 text-sm">{formatCurrency(contribution.amount)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900 text-sm">Total Employer Contributions</span>
                    <span className="font-bold text-apollo-primary text-sm">
                      {formatCurrency(payslipData.employerContributions.reduce((sum, item) => sum + item.amount, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 text-xs text-gray-500 text-center">
            <p>This is a computer-generated payslip and does not require a signature.</p>
            <p className="mt-1">For any queries regarding your salary, please contact the HR department.</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PayslipDetail;
